/**
 * The ONE client-side HTML subset for operator-authored rich text.
 *
 * This tag list IS the `SPEC-FRONTEND-RULE-023` tag set - `p`, `br`, `a`,
 * `strong`, `em`, `ul`, `ol`, `li`, `span`, with `href` and the gateway's forced
 * `rel` as the only attributes. It is not "a sensible default" that happens to
 * look like the rule; changing it changes what the rule permits.
 *
 * WHY A SECOND PASS EXISTS AT ALL. The gateway already sanitises every value of
 * `legal_footer.line.*` / `legal_footer.impressum.*` ON WRITE with a hand-built
 * `bluemonday` policy carrying exactly this tag set
 * (`SPEC-PLATFORM-BRANDING` RULE-003), so nothing unsanitised reaches
 * `GET /api/v1/branding`. The client pass here is DEFENCE IN DEPTH, plus the one
 * place where it is not redundant at all: `AppFooter` also renders the System
 * Settings live preview from UNSAVED draft values that no server has ever seen
 * (`SPEC-PLATFORM-BRANDING` SCENARIO-016), and it renders on the pre-auth login
 * page where no principal exists.
 *
 * ONE LIST, TWO CALLERS, AND A TEST THAT KEEPS IT ONE LIST. `AppFooter` and the
 * public `ChatWidget` footer (`SPEC-FRONTEND-RULE-023` `footerHtml`) both consume
 * this module. This file USED to claim that the client list and the gateway's
 * `bluemonday` policy "can only drift by someone editing two files on purpose".
 * That claim was false, and the Wave 3 audit measured it drifting FOUR ways with
 * nobody editing two files: the gateway forced a `rel` this list then stripped,
 * and DOMPurify's own sealed URI expression passed `tel:`, `ftp://` and relative
 * hrefs that the gateway refuses. The claim rested on an unverified belief about
 * a library default, so a belief is not what holds the two sides together any
 * more: `services/api-gateway/routes/branding_policy_parity_test.go` READS the
 * three constants below and fails on any difference from the Go policy (D-BR-22,
 * the first step; the generated-artefact shape waits on a prevalence scan over
 * the tree's three HTML policies).
 *
 * ONE DIVERGENCE REMAINS, AND IT IS DECLARED RATHER THAN CLAIMED CLOSED.
 * DOMPurify's attribute allow-list is GLOBAL, not per element, so `href` on a
 * non-anchor (`<span href="...">`) survives this pass where the gateway allows it
 * only on `<a>` (`AllowAttrs("href").OnElements("a")`). An `href` on a `<span>`
 * is inert - no navigation, no scheme resolution, no script - and closing it here
 * would mean a DOMPurify hook, which is a second policy object of exactly the
 * kind this module exists to prevent. The parity test therefore compares the tag
 * set, the attribute set and the URI scheme set, and does NOT claim per-element
 * parity; this paragraph is the record of what it does not cover.
 *
 * A CONSUMER THAT NEEDS A WIDER LIST NEEDS A RULE CHANGE, NOT A SECOND
 * CONSTANT. Do not copy these arrays, do not extend them at a call site, and do
 * not add an `ADD_TAGS` / `ADD_ATTR` option beside them: a second list is the
 * exact drift this module exists to make impossible. Widen RULE-023 and RULE-003
 * together, then widen this file once.
 *
 * @see SPEC-FRONTEND-RULE-023 - the tag set of record.
 * @see SPEC-PLATFORM-BRANDING-RULE-003 - sanitised on write, one tag list (D-BR-07).
 * @see TASK-PB-APPFOOTER-COMPONENT
 * @see TASK-PB-FOOTER-AUDIT-FIXES-CHG-001 - the forced `rel`, the URI allow-list.
 */

import DOMPurify from 'dompurify';

/**
 * The only HTML elements an operator-authored value may contain.
 *
 * Frozen so a caller cannot mutate the shared array in place; spread it into a
 * fresh array when a library demands a mutable one.
 */
export const HTML_SUBSET_ALLOWED_TAGS = Object.freeze([
  'p',
  'br',
  'a',
  'strong',
  'em',
  'ul',
  'ol',
  'li',
  'span',
] as const);

/**
 * The only attributes an operator-authored value may carry.
 *
 * `href` and `rel` - `target` and every other attribute are dropped, matching
 * the gateway policy.
 *
 * `rel` IS NOT A WIDENING (audit P1-3). The gateway FORCES
 * `rel="noopener noreferrer"` onto every anchor it stores and refuses the whole
 * write rather than keep an anchor it could not stamp (`forceAnchorRel`,
 * `services/api-gateway/routes/branding.go`), because the footer renders on the
 * pre-authentication login page where an operator link must never hand the
 * opener a window reference or a referrer. Until this entry existed the browser
 * pass removed the attribute again before the one HTML sink in `AppFooter`, so
 * the control the rule goes to real trouble over was inert exactly where the
 * rule names its reason. The attribute carries no scheme and no script, and on
 * this path the gateway is its only writer.
 */
export const HTML_SUBSET_ALLOWED_ATTR = Object.freeze(['href', 'rel'] as const);

/**
 * The only URI schemes an operator-authored `href` may carry - the client mirror
 * of the gateway's `AllowURLSchemes('http', 'https', 'mailto')` declaration.
 *
 * IT IS DECLARED HERE BECAUSE DOMPURIFY'S DEFAULT IS WIDER THAN THE GATEWAY'S,
 * which is the P2-1 half of the audit finding. The sealed default expression in
 * the installed build (dompurify 3.4.7, `dist/purify.cjs.js:325`) is
 * `/^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i`
 * - it admits `ftp:`, `tel:`, `callto:`, `sms:`, `cid:`, `xmpp:`, `matrix:` and
 * every relative value, all of which the gateway strips. It refuses
 * `javascript:` and `data:`, which is the half the old comment here got right
 * and then generalised into a claim of parity that was never true.
 *
 * NARROWING ONLY. Every scheme this list removes was accepted by the browser
 * pass and refused by the server pass, so no value that could be STORED stops
 * rendering; a `mailto:` contact link and an `https:` notice - the two
 * SCENARIO-013 asserts positively - both stay.
 */
export const HTML_SUBSET_ALLOWED_URI_SCHEMES = Object.freeze([
  'http',
  'https',
  'mailto',
] as const);

/**
 * {@link HTML_SUBSET_ALLOWED_URI_SCHEMES} as the expression DOMPurify takes.
 *
 * DERIVED from the list rather than typed beside it, so the two cannot disagree:
 * the list is the declaration the parity test reads, and this is the only thing
 * built from it. A relative href matches nothing here and is dropped, exactly as
 * bluemonday drops it (the gateway does not declare `AllowRelativeURLs`).
 */
export const HTML_SUBSET_ALLOWED_URI_REGEXP = new RegExp(
  `^(?:${HTML_SUBSET_ALLOWED_URI_SCHEMES.join('|')}):`,
  'i',
);

/**
 * Sanitise operator-authored HTML down to {@link HTML_SUBSET_ALLOWED_TAGS},
 * {@link HTML_SUBSET_ALLOWED_ATTR} and {@link HTML_SUBSET_ALLOWED_URI_SCHEMES}.
 *
 * Pure and synchronous: it neither fetches nor caches. Callers that render the
 * result into a `dangerouslySetInnerHTML` sink should memoise the call on the
 * input string rather than re-purifying unchanged HTML on every parent render.
 *
 * `ADD_URI_SAFE_ATTR: ['rel']` IS LOAD-BEARING, NOT DECORATION. DOMPurify runs
 * `ALLOWED_URI_REGEXP` against EVERY allowed attribute that is not on its
 * URI-safe list, and `rel` is not on it (`DEFAULT_URI_SAFE_ATTRIBUTES`,
 * `dist/purify.cjs.js:597`). Without this line the narrowed expression rejects
 * the value `noopener noreferrer` - it carries no scheme - and the two fixes of
 * this TASK cancel each other out: the attribute is allowed and then stripped
 * again. Verified against the installed build before it was written, not
 * assumed. It declares "this attribute is not a URI"; it does not widen the
 * attribute list, which is {@link HTML_SUBSET_ALLOWED_ATTR} and nothing else.
 *
 * @param raw - The HTML to sanitise. A nullish or empty input returns `''`.
 * @returns The sanitised HTML, safe for a `dangerouslySetInnerHTML` sink.
 */
export function sanitizeHtmlSubset(raw: string | null | undefined): string {
  if (!raw) return '';
  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: [...HTML_SUBSET_ALLOWED_TAGS],
    ALLOWED_ATTR: [...HTML_SUBSET_ALLOWED_ATTR],
    ALLOWED_URI_REGEXP: HTML_SUBSET_ALLOWED_URI_REGEXP,
    ADD_URI_SAFE_ATTR: ['rel'],
  });
}
